import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usuariosService, Usuario } from '../services/api/usuariosService';
import { authService } from '../services/authService';
import { useOraclePhoto } from '../hooks/useOraclePhoto'; // Hook para URLs do Oracle Storage
import { oracleUrlCache } from '../services/oracleUrlCache'; // ✅ NOVO: Serviço de cache Oracle

const FormUsuario: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  // CORREÇÃO: Estados simplificados para upload
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  
  // ✅ CORREÇÃO FASE 2: Inicializar usuário sem foto_perfil
  const [usuario, setUsuario] = useState<Usuario>({
    nome: '',
    usuario: '',
    email: '',
    senha: '',
    cargo: 'psicologo',
    cod_empresa: 0,
    ativo: true,
    crp: '',
    telefone: ''
  });
  
  const [confirmarSenha, setConfirmarSenha] = useState('');
  
  // ✅ NOVO: Controlar se checkbox 'ativo' foi explicitamente modificado
  const [ativoFoiModificado, setAtivoFoiModificado] = useState(false);
  
  // Obter empresa do usuário atual
  const usuarioAtual = authService.getUsuario();
  const cod_empresa = usuarioAtual?.cod_empresa;
  
  // Hook para buscar URL assinada do Oracle Storage (se usuário tiver storage_key)
  const hasStorageKey = !!(usuario.foto_perfil_storage_key);
  
  const { 
    url: oraclePhotoUrl, 
    loading: oraclePhotoLoading 
  } = useOraclePhoto(
    id ? parseInt(id) : undefined,
    hasStorageKey, // Só buscar se tiver storage_key
    '/img/avatar.svg' // Fallback SOMENTE para avatar padrão (SEM fotos locais)
  );
  
  useEffect(() => {
    let isMounted = true;
    
    console.log('🔍 FormUsuario useEffect executado!', { 
      id, 
      typeOfId: typeof id,
      idAsNumber: Number(id),
      isValidId: !isNaN(Number(id)),
      location: window.location.href,
      params: { id },
      existeId: !!id,
      idLength: id ? id.length : 0
    });
    
    const carregarUsuario = async () => {
      // Só carrega o usuário se estiver editando (id presente)
      if (!id) {
        // Inicializar com a empresa do usuário atual
        if (cod_empresa) {
          setUsuario(prevState => ({ ...prevState, cod_empresa }));
        }
        return;
      }
      
      try {
        setLoading(true);
        setLoadError(null);
        
        // Adicionar logging para debug
        // Tentar carregar várias vezes em caso de erro
        let tentativas = 0;
        let userData = null;
        
        while (tentativas < 3 && !userData) {
          try {
            if (tentativas > 0) {
              // Pequena pausa entre tentativas
              await new Promise(r => setTimeout(r, 1000));
            }
            
            // Se tiver cache no localStorage, usar temporariamente enquanto carrega
            const cachedUser = localStorage.getItem(`user_${id}`);
            if (cachedUser && tentativas === 0) {
              try {
                const parsedUser = JSON.parse(cachedUser);
                // Atualizar UI rapidamente com dados em cache
                if (isMounted) {
                  setUsuario(parsedUser);
                }
              } catch (e) {
                console.error('[FormUsuario] Erro ao usar cache:', e);
              }
            }
            
            userData = await usuariosService.obterUsuario(id);
            // Guardar em cache local por 5 minutos
            localStorage.setItem(`user_${id}`, JSON.stringify(userData));
            setTimeout(() => localStorage.removeItem(`user_${id}`), 5 * 60 * 1000);
            
            break;
          } catch (retryError) {
            tentativas++;
            if (tentativas >= 3) throw retryError;
          }
        }
        
        // Verificar se o componente ainda está montado
        if (isMounted && userData) {
          setUsuario(userData);
          setLoading(false);
        }
      } catch (error) {
        console.error('[FormUsuario] Erro ao carregar dados do usuário:', error);
        
        if (isMounted) {
          let errorMsg = 'Erro ao carregar dados do usuário. Tente novamente.';
          if (error instanceof Error) errorMsg += ' ' + error.message;
          
          setLoadError(errorMsg);
          setLoading(false);
        }
      }
    };
    
    carregarUsuario();
    
    return () => {
      isMounted = false;
    };
  }, [id, cod_empresa]);
  
  // CORREÇÃO: Simplificar handleChange removendo lógica de arquivo
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    
    if (type === 'checkbox') {
      const { checked } = e.target as HTMLInputElement;
      setUsuario(prevState => ({ ...prevState, [name]: checked }));
      
      // ✅ Marcar que checkbox 'ativo' foi explicitamente modificado pelo usuário
      if (name === 'ativo') {
        setAtivoFoiModificado(true);
        }
    } else {
      // CORREÇÃO: Remover lógica de arquivo daqui, será tratada em handleImageChange
      setUsuario(prevState => ({ ...prevState, [name]: value }));
    }
  };
  
  // CORREÇÃO: Handler de upload simplificado
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    
    // Limpar estados anteriores
    setImageError(null);
    setImagePreview(null);
    setSelectedFile(null);
    
    if (!file) {
      return;
    }

    // Validações
    if (file.size > 2 * 1024 * 1024) {
      setImageError('A imagem deve ter no máximo 2MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setImageError('O arquivo deve ser uma imagem');
      return;
    }

    // CORREÇÃO: Apenas criar preview local, não converter para base64
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
    setSelectedFile(file);
    
    };

  // CORREÇÃO: Remover imagem
  const handleRemoveImage = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    
    setImagePreview(null);
    setSelectedFile(null);
    setImageError(null);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    
    // Validar senha
    if (!id && usuario.senha !== confirmarSenha) {
      setSubmitError('As senhas não conferem');
      return;
    }
    
    if (!id && (!usuario.senha || usuario.senha.length < 6)) {
      setSubmitError('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    // Validar campos obrigatórios
    if (!usuario.nome || !usuario.usuario || !usuario.email || !usuario.cargo) {
      setSubmitError('Todos os campos são obrigatórios');
      return;
    }
    
    // Adicionar cod_empresa se não existir
    if (!usuario.cod_empresa && cod_empresa) {
      setUsuario(prevState => ({ ...prevState, cod_empresa }));
    }
    
    try {
      setLoading(true);
      
      // CORREÇÃO: Usar FormData para upload de arquivo
      const formData = new FormData();
      
      // Adicionar dados do usuário
      formData.append('nome', usuario.nome);
      formData.append('email', usuario.email);
      formData.append('usuario', usuario.usuario);
      formData.append('cargo', usuario.cargo);
      
      // ✅ CORREÇÃO CRÍTICA: Só enviar 'ativo' se foi EXPLICITAMENTE modificado pelo usuário
      // Evita desativar usuário acidentalmente ao trocar apenas a foto
      if (id && ativoFoiModificado) {
        formData.append('ativo', usuario.ativo.toString());
        } else if (id) {
        }
      
      if (usuario.crp) {
        formData.append('crp', usuario.crp);
      }
      
      if (usuario.telefone) {
        formData.append('telefone', usuario.telefone);
      }
      
      // CORREÇÃO: Adicionar arquivo apenas se selecionado
      if (selectedFile) {
        formData.append('foto_perfil', selectedFile);
        }
      
      // CORREÇÃO: Adicionar senha apenas para criação
      if (!id && usuario.senha) {
        formData.append('senha', usuario.senha);
      }
      
      if (id) {
        try {
          await usuariosService.atualizarUsuarioComArquivo(id, formData);
          if (selectedFile) {
            oracleUrlCache.remove('usuario_foto', id);
          }
          alert('Usuário atualizado com sucesso!');
        } catch (err: any) {
          const mensagem = err?.friendlyMessage || 'Erro ao atualizar usuário.';
          alert(mensagem);
          throw err;
        }
      } else {
        try {
          const novoUsuario = await usuariosService.criarUsuarioComArquivo(formData);
          if (selectedFile && novoUsuario?.id) {
            oracleUrlCache.remove('usuario_foto', novoUsuario.id);
          }
          alert('Usuário criado com sucesso!');
        } catch (err: any) {
          const mensagem = err?.friendlyMessage || 'Erro ao criar usuário.';
          alert(mensagem);
          throw err;
        }
      }
      
      navigate('/usuarios');
    } catch (error: any) {
      console.error('Erro ao salvar usuário:', error);
      setSubmitError(error?.friendlyMessage || 'Erro ao salvar usuário. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };
  
  // ✅ Enhanced getImageUrl function - SOMENTE Oracle Storage ou Avatar
  const getImageUrl = (): string | undefined => {
    console.log('[FormUsuario - getImageUrl] 🎯 Determinando URL da foto:', {
      imagePreview: !!imagePreview,
      hasStorageKey: !!usuario.foto_perfil_storage_key,
      storageKey: usuario.foto_perfil_storage_key,
      oraclePhotoUrl: oraclePhotoUrl?.substring(0, 50),
      oraclePhotoLoading
    });
    
    // PRIORIDADE 1: Preview local (upload em andamento)
    if (imagePreview) {
      console.log('[FormUsuario] 📷 Usando preview local (upload)');
      return imagePreview;
    }
    
    // PRIORIDADE 2: URL do Oracle Storage (se tiver storage_key)
    if (usuario.foto_perfil_storage_key) {
      if (oraclePhotoUrl && oraclePhotoUrl !== '/img/avatar.svg') {
        console.log('[FormUsuario] ✅ Usando URL Oracle:', oraclePhotoUrl.substring(0, 80) + '...');
        return oraclePhotoUrl;
      } else {
        // Hook ainda está carregando ou retornou fallback
        if (oraclePhotoLoading) {
          return undefined; // Mostrar placeholder enquanto carrega
        }
        // Se não está carregando mas retornou fallback, pode ser erro temporário
        console.log('[FormUsuario] 📸 Usando avatar temporário (Oracle não disponível)');
        return '/img/avatar.svg';
      }
    }
    
    // PRIORIDADE 3: Avatar padrão (sem storage_key)
    console.log('[FormUsuario] 👤 Usando avatar padrão (sem storage_key)');
    return '/img/avatar.svg';
  };
  
  if (loading && !usuario.nome) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border" role="status">
          <span className="sr-only">Carregando...</span>
        </div>
      </div>
    );
  }
  
  if (loadError) {
    return (
      <div className="alert alert-danger" role="alert">
        <h4 className="alert-heading">Erro ao carregar usuário!</h4>
        <p>{loadError}</p>
        <hr />
        <div className="d-flex justify-content-between">
          <button 
            className="btn btn-outline-danger"
            onClick={() => navigate('/usuarios')}
          >
            Voltar para Lista
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }
  
  // ✅ Função para tratar erro na imagem - SEM DIAGNÓSTICO AUTOMÁTICO
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const imgElement = e.target as HTMLImageElement;
    
    // Fallback para avatar padrão (SVG que realmente existe)
    imgElement.src = '/img/avatar.svg';
    
    // Prevenir loops infinitos de erro
    imgElement.onerror = null;
    
    };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                {id ? 'Editar Usuário' : 'Novo Usuário'}
              </h3>
            </div>
            
            <div className="card-body">
              {loadError && (
                <div className="alert alert-danger">{loadError}</div>
              )}
              
              {submitError && (
                <div className="alert alert-danger">{submitError}</div>
              )}
              
              <form onSubmit={handleSubmit}>
                {/* CORREÇÃO: Seção de upload simplificada */}
                <div className="row mb-4">
                  <div className="col-md-12">
                    <div className="text-center">
                      <h5 className="mb-3">Foto de Perfil</h5>
                      
                      {/* Preview da imagem */}
                      {(imagePreview || usuario.foto_perfil_storage_key || oraclePhotoUrl) ? (
                        <div className="position-relative d-inline-block">
                          {oraclePhotoLoading && !imagePreview ? (
                            <div 
                              className="img-circle elevation-2 d-flex align-items-center justify-content-center bg-light"
                              style={{ width: '150px', height: '150px' }}
                            >
                              <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Carregando foto...</span>
                              </div>
                            </div>
                          ) : (
                            <img 
                              src={getImageUrl()}
                              alt="Foto de perfil" 
                              className="img-circle elevation-2" 
                              style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                              onError={handleImageError}
                            />
                          )}
                          <button 
                            type="button"
                            className="btn btn-sm btn-danger position-absolute"
                            style={{ top: 0, right: 0, borderRadius: '50%' }}
                            onClick={handleRemoveImage}
                            title="Remover foto"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ) : (
                        <div className="img-circle elevation-2 d-flex align-items-center justify-content-center bg-light" 
                             style={{ width: '150px', height: '150px', margin: '0 auto' }}>
                          <i className="fas fa-user fa-5x text-secondary"></i>
                        </div>
                      )}
                      
                      {/* Input de arquivo */}
                      <div className="mt-3">
                        <input 
                          ref={fileInputRef}
                          type="file"
                          className="d-none"
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                        <button 
                          type="button" 
                          className="btn btn-outline-primary"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <i className="fas fa-camera mr-2"></i>
                          {imagePreview || usuario.foto_perfil_storage_key ? 'Alterar Foto' : 'Adicionar Foto'}
                        </button>
                        
                        {imageError && (
                          <div className="text-danger mt-2 small">
                            <i className="fas fa-exclamation-triangle mr-1"></i>
                            {imageError}
                          </div>
                        )}
                        
                        {selectedFile && (
                          <div className="text-success mt-2 small">
                            <i className="fas fa-check mr-1"></i>
                            Arquivo selecionado: {selectedFile.name}
                          </div>
                        )}
                        
                        <div className="text-muted mt-1 small">
                          Tamanho máximo: 2MB. Formatos: JPG, PNG, GIF.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="row">
                  {/* Dados pessoais */}
                  <div className="col-md-6">
                    <h5 className="mb-3">Dados Pessoais</h5>
                    
                    <div className="form-group">
                      <label htmlFor="nome">Nome Completo</label>
                      <input
                        type="text"
                        className="form-control"
                        id="nome"
                        name="nome"
                        value={usuario.nome}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="email">Email</label>
                      <input
                        type="email"
                        className="form-control"
                        id="email"
                        name="email"
                        value={usuario.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="crp">CRP (Registro Profissional)</label>
                      <input
                        type="text"
                        className="form-control"
                        id="crp"
                        name="crp"
                        value={usuario.crp || ''}
                        onChange={handleChange}
                        placeholder="Apenas para psicólogos"
                      />
                      <small className="form-text text-muted">
                        Formato: 00/00000. Obrigatório apenas para cargo de psicólogo.
                      </small>
                    </div>
                  </div>
                  
                  {/* Dados de acesso */}
                  <div className="col-md-6">
                    <h5 className="mb-3">Dados de Acesso</h5>
                    
                    <div className="form-group">
                      <label htmlFor="usuario">Nome de Usuário</label>
                      <input
                        type="text"
                        className="form-control"
                        id="usuario"
                        name="usuario"
                        value={usuario.usuario}
                        onChange={handleChange}
                        required
                        disabled={!!id}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="cargo">Cargo</label>
                      <select
                        className="form-control"
                        id="cargo"
                        name="cargo"
                        value={usuario.cargo}
                        onChange={handleChange}
                        required
                      >
                        <option value="admin">Administrador</option>
                        <option value="psicologo">Psicólogo</option>
                        <option value="estudante">Estudante</option>
                        <option value="atendente">Atendente</option>
                      </select>
                    </div>
                    
                    {!id ? (
                      // Campos de senha apenas para novo usuário
                      <>
                        <div className="form-group">
                          <label htmlFor="senha">Senha</label>
                          <input
                            type="password"
                            className="form-control"
                            id="senha"
                            name="senha"
                            value={usuario.senha}
                            onChange={handleChange}
                            required={!id}
                            minLength={6}
                          />
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor="confirmarSenha">Confirmar Senha</label>
                          <input
                            type="password"
                            className="form-control"
                            id="confirmarSenha"
                            value={confirmarSenha}
                            onChange={(e) => setConfirmarSenha(e.target.value)}
                            required={!id}
                          />
                        </div>
                      </>
                    ) : (
                      // Opção de resetar senha para usuário existente
                      <div className="alert alert-info">
                        Para redefinir a senha, use a opção "Resetar Senha" na lista de usuários.
                      </div>
                    )}
                    
                    {id && (
                      <div className="form-group">
                        <div className="custom-control custom-switch">
                          <input
                            type="checkbox"
                            className="custom-control-input"
                            id="ativo"
                            name="ativo"
                            checked={usuario.ativo}
                            onChange={handleChange}
                          />
                          <label className="custom-control-label" htmlFor="ativo">
                            Usuário Ativo
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="form-group mt-4 text-center">
                  <button type="submit" className="btn btn-primary btn-lg" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                        Salvando...
                      </>
                    ) : (
                      'Salvar'
                    )}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary btn-lg ml-2"
                    onClick={() => navigate('/usuarios')}
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormUsuario;
